import { Box, Tooltip } from "@mui/material";
import { blue, green, orange, red } from "@mui/material/colors";
import {
  CheckCircleOutline,
  ErrorOutline,
  HourglassEmpty,
  LinkRounded,
  PersonOutline,
  ReportProblemOutlined,
  VerifiedUserOutlined,
} from "@mui/icons-material";
import { memo, useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import AssociationsEditor from "../../../../../components/associations/AssociationsEditor";
import { filterAssociationFields } from "../../../../../components/associations/config/associationFieldsConfig";
import useAssociationUtils from "../../../../../components/associations/hooks/useAssociationUtils";
import ApplicantSendActionsButton from "../../../buttons/ApplicantSendActionsButton";
import PreviewableLoanFileButton from "../../../../../components/common/Loan/buttons/PreviewableLoanFileButton";
import Flx from "../../../../../components/layout/Flx";
import Txt from "../../../../../components/typography/Txt";
import CardPaper from "../../../../../components/ui/CardPaper";
import { formatDate } from "../../../../../_src_shared/utils/functions/formatDate";
import { isDocHidden } from "../../../../../_src_shared/utils/functions/isDocHidden";
import { useUnderwritingHook } from "../../../../../hooks/useUnderwritingHook";
import { loanDrilldownSet } from "../../../../../redux/actions/loanDrilldownActions";
import RecordDocCoveragePanel, {
  DOC_PANEL_MAX_H,
} from "../../../shared/RecordDocCoveragePanel";
import { fmtPoints } from "../../../experience/experienceConstants";
import { teal } from "../../../../../theme/colors";
import PriorLoansSection from "../../../applicantHistory/PriorLoansSection";
import VerificationField from "./VerificationField";
import {
  VERIFICATIONS_BY_KEY,
  useVerificationDocs,
} from "../../../verificationDocs/verificationConfig";
import ApplicantCreditedExperience from "./ApplicantCreditedExperience";

// Design tokens — mirror PropertyCard so the two redesigned cards read
// as one visual language.
const TOKENS = {
  surface: "#ffffff",
  surface2: "#f3f5f8",
  surface3: "#f7f9fb",
  ink: "#0f172a",
  ink2: "#334155",
  ink3: "#64748b",
  ink4: "#94a3b8",
  line: "#d8e0ea",
  line2: "#e6ebf2",
  accent: "#1a3d2e",
  accentSoft: "#e8f0eb",
  accentLine: "#c5dccd",
  warn: "#b8641d",
  warnSoft: "#fbf0e2",
  warnLine: "#e6c8a3",
  urgent: "#a82a2a",
  urgentSoft: "#fae8e8",
  good: "#2d6a4a",
  goodSoft: "#e3efe7",
  goodLine: "#c5dccd",
  info: "#2c5282",
  infoSoft: "#e6edf5",
  infoLine: "#c7d6e9",
};

const SHADOW_MD =
  "0 2px 4px rgba(15, 23, 42, 0.05), 0 6px 16px rgba(15, 23, 42, 0.07)";
const SHADOW_LG =
  "0 4px 12px rgba(15, 23, 42, 0.08), 0 16px 40px rgba(15, 23, 42, 0.10)";
const FONT_SERIF = '"Fraunces", "Source Serif Pro", Georgia, serif';
const FONT_MONO = '"JetBrains Mono", "SF Mono", Menlo, monospace';

// Borrower classification A+/A/B/C carry their own color story — separate
// from the generic SUB_TAG_TONES so the rating reads as a quality grade.
const CLASS_TONE = {
  "A+": { fg: green[800], bg: green[50], border: green[200] },
  A: { fg: blue[800], bg: blue[50], border: blue[200] },
  B: { fg: orange[800], bg: orange[50], border: orange[200] },
  C: { fg: red[800], bg: red[50], border: red[200] },
};

const fmtMoney = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n) || n === 0) return "$0";
  return `$${n.toLocaleString()}`;
};

const fmtNumber = (v, suffix = "") => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n) || n === 0) return "—";
  return `${n.toLocaleString()}${suffix}`;
};

// Credit auth / credit report / background are DATE-ONLY fields, so they format
// through `formatDate` (UTC-pinned) — NOT `formatDateTime` (Eastern), which
// would shift the calendar date back a day. See the "Dates & times" convention
// in CLAUDE.md. Avoid bare `new Date(...)` for date display.
const fmtDateShort = (v) => {
  if (!v) return null;
  return formatDate(v, null, "MMM d, yyyy") || null;
};

// Documents render in their own panel on the right — keeping them out of
// the chip stack avoids duplicate display and lets the chip section
// focus on related parties / properties.
const ASSOCIATION_FIELDS = ["subjectProperties", "entities"];

// The card's three verification boxes; the definitions (targets, matchers,
// what each date means) live in verificationConfig.js, shared with the
// applicant sidebar's Key Docs & Reports tab.
const CARD_VERIFICATIONS = ["creditAuth", "creditReport", "background"].map(
  (k) => VERIFICATIONS_BY_KEY[k],
);

const ApplicantCard = memo(
  ({ borrower, isSelected, onClick, priorLoans, experience }) => {
  const dispatch = useDispatch();
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const loanId = loanDrilldown?._id;
  const { addAssociation } = useUnderwritingHook();
  const { getAssociationDocumentsByBorrowerId } = useAssociationUtils();

  const borrowerFiles = useMemo(
    () =>
      (
        getAssociationDocumentsByBorrowerId(loanDrilldown, borrower?._id) || []
      ).filter((f) => !isDocHidden(f)),
    [getAssociationDocumentsByBorrowerId, loanDrilldown, borrower?._id],
  );

  // Per verification: the applicant's files of that type, and every loan
  // document of that type (shared with the sidebar).
  const verificationDocs = useVerificationDocs(loanDrilldown, borrower?._id);

  const [editorOpen, setEditorOpen] = useState(false);
  const [savingAssociations, setSavingAssociations] = useState(false);

  const handleOpenEditor = useCallback((e) => {
    e?.stopPropagation();
    setEditorOpen(true);
  }, []);

  const handleSaveAssociations = useCallback(
    (selected) => {
      if (!loanId || !borrower?._id) return;
      const cleaned = ASSOCIATION_FIELDS.reduce((acc, key) => {
        if (Array.isArray(selected?.[key]) && selected[key].length > 0) {
          acc[key] = selected[key];
        }
        return acc;
      }, {});
      if (Object.keys(cleaned).length === 0) {
        setEditorOpen(false);
        return;
      }
      setSavingAssociations(true);
      addAssociation({
        dealId: loanId,
        associationTypeName: "borrowers",
        associationTypeId: borrower._id,
        associationData: cleaned,
        onSuccessFn: (updatedLoan) => {
          dispatch(loanDrilldownSet(updatedLoan));
          setSavingAssociations(false);
          setEditorOpen(false);
        },
        onFailFn: () => setSavingAssociations(false),
      });
    },
    [loanId, borrower?._id, addAssociation, dispatch],
  );

  // Hooks below need to run on every render — declared before the
  // `if (!borrower)` early-return so React's hook-order invariant
  // doesn't trip when a borrower briefly transitions to null.
  const subTags = useMemo(() => {
    const items = [];
    if (borrower?.rtlBorrowerClassification) {
      items.push({
        key: "class",
        label: borrower.rtlBorrowerClassification,
        kind: "class",
        tooltip: classTooltip(borrower, loanDrilldown?.loanProductType),
      });
    }
    // "Primary applicant" follows the borrower's "Is primary contact for the
    // loan" checkbox, not position in the list. Guarantor is independent —
    // a primary contact can also be a guarantor.
    if (borrower?.isPrimaryContactForLoan) {
      items.push({ key: "primary", label: "Primary applicant", tone: "muted" });
    }
    if (borrower?.isGuarantor) {
      items.push({ key: "guarantor", label: "Guarantor", tone: "muted" });
    }
    if (borrower?.repeatBorrowerFlag) {
      items.push({ key: "repeat", label: "Repeat", tone: "info" });
    }
    if (borrower?.foreignNationalFlag) {
      items.push({
        key: "foreign-national",
        label: "Foreign National",
        tone: "warn",
      });
    }
    // Linked-portal-user pill — surfaces whether this applicant has been
    // linked to a borrower-portal account so underwriters can see at a
    // glance which applicants can self-serve uploads / messages.
    if (borrower?.borrowerPortalUserId) {
      items.push({
        key: "portal-linked",
        label: "Portal linked",
        tone: "good",
      });
    }
    return items;
    // The class tooltip reads several borrower fields, so key on the record.
  }, [borrower, loanDrilldown?.loanProductType]);

  // Linked properties / entities (never documents) — shown as header chips.
  const linkedItems = useLinkedItems(loanDrilldown, borrower?.associations);

  if (!borrower) return null;

  const fullName = [borrower?.firstName, borrower?.lastName]
    .filter(Boolean)
    .join(" ");
  const initials =
    `${borrower?.firstName?.[0] || ""}${borrower?.lastName?.[0] || ""}`.toUpperCase();
  const ficoMid = borrower?.ficoMid || 0;
  const totalLiquidity = Number(borrower?.totalLiquidity) || 0;
  const verifiedExp = Number(borrower?.verifiedExperienceTotal) || 0;
  const ownershipPct = borrower?.entityPercentOwnership;
  const mortgageLates = Array.isArray(borrower?.mortgage_lates)
    ? borrower.mortgage_lates.length
    : Number(borrower?.mortgage_lates) || 0;
  const creditAuthDate = borrower?.creditAuthDate;
  const backgroundDate = borrower?.backgroundDate;
  const creditReportDate = borrower?.creditReportDate;

  const ficoColor = (() => {
    if (!ficoMid) return TOKENS.ink3;
    if (ficoMid >= 720) return TOKENS.good;
    if (ficoMid >= 680) return TOKENS.warn;
    return TOKENS.urgent;
  })();
  const ficoHighlight = ficoMid >= 720;
  const expColor = (() => {
    if (verifiedExp >= 3) return TOKENS.good;
    if (verifiedExp > 0) return TOKENS.warn;
    return TOKENS.ink3;
  })();

  return (
    <CardPaper
      selected={isSelected}
      onClick={onClick}
      sx={{
        position: "relative",
        border: `1px solid ${TOKENS.line}`,
        boxShadow: SHADOW_MD,
        cursor: "pointer",
        overflow: "hidden",
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" },
        // gridTemplateColumns: { xs: "1fr", md: "1.55fr 1fr" },
        transition:
          "box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease",
        "&:hover": {
          boxShadow: SHADOW_LG,
          borderColor: TOKENS.ink4,
          transform: "translateY(-2px)",
          // The theme's primary-button hover (teal-700).
          "& .open-indicator": {
            background: teal[700],
            borderColor: teal[700],
          },
          "& .open-indicator-arrow": {
            transform: "translateX(2px)",
          },
        },
      }}
    >
      {/* ═══════════════════════════════════════════
          LEFT COLUMN — header + figures + associated + status block
          ═══════════════════════════════════════════ */}
      <Box
        sx={{
          p: { xs: "22px", md: "28px 32px" },
          display: "flex",
          flexDirection: "column",
          gap: 3,
          borderRight: { md: `1px solid ${TOKENS.line}` },
          borderBottom: { xs: `1px solid ${TOKENS.line}`, md: "none" },
          minWidth: 0,
        }}
      >
        {/* HEADER */}
        <Flx
          g={2.5}
          sx={{ alignItems: "flex-start", justifyContent: "space-between" }}
        >
          <Flx g={1.75} sx={{ alignItems: "flex-start", flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: "10px",
                background: `linear-gradient(135deg, ${TOKENS.surface2} 0%, ${TOKENS.accentSoft} 100%)`,
                border: `1px solid ${TOKENS.line}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                color: TOKENS.accent,
                fontFamily: FONT_MONO,
                fontWeight: 600,
                fontSize: 16,
                letterSpacing: "0.02em",
              }}
            >
              {initials || <PersonOutline sx={{ fontSize: 22 }} />}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Txt
                component="h2"
                sx={{
                  fontFamily: FONT_SERIF,
                  fontWeight: 500,
                  fontSize: 24,
                  letterSpacing: "-0.02em",
                  m: 0,
                  mb: "4px",
                  color: TOKENS.ink,
                  lineHeight: 1.15,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {fullName || "Unnamed Applicant"}
              </Txt>
              <Flx ac wrap g={1} sx={{ minWidth: 0 }}>
                {(borrower?.company || borrower?.emailAddress) && (
                  <Txt
                    sx={{
                      fontSize: 13,
                      color: TOKENS.ink3,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: 280,
                    }}
                  >
                    {borrower?.company || borrower?.emailAddress}
                  </Txt>
                )}
                {subTags.length + linkedItems.length > 0 && (
                  <>
                    <Box
                      sx={{
                        width: 3,
                        height: 3,
                        borderRadius: "50%",
                        background: TOKENS.ink4,
                      }}
                    />
                    {subTags.map((t) => (
                      <SubTag
                        key={t.key}
                        tone={t.tone}
                        kind={t.kind}
                        tooltip={t.tooltip}
                      >
                        {t.label}
                      </SubTag>
                    ))}
                    {/* Associations (properties / entities) close the chip
                        row; a click opens the associations editor. */}
                    {linkedItems.map((item) => (
                      <LinkedTag
                        key={item.key}
                        item={item}
                        onClick={handleOpenEditor}
                      />
                    ))}
                  </>
                )}
              </Flx>
            </Box>
          </Flx>

          {/* HEADER ACTIONS — the send/link dropdown sits to the left of the
              open indicator, on the same baseline. The dropdown stops click
              propagation; the card itself is a click target.

              Lendmarq divergence (see CLAUDE.md #4): this button does not exist
              in Eastview, and this file is otherwise EV-synced — a merge that
              takes EV's version of ApplicantCard.js will drop it. Re-add. */}
          <Flx ac g={1} sx={{ flexShrink: 0 }}>
          <ApplicantSendActionsButton borrower={borrower} />
          {/* OPEN INDICATOR — `View details →` mono pill that color-shifts
              and the arrow nudges right when the card is hovered. */}
          <Flx
            ac
            g={0.75}
            className="open-indicator"
            sx={{
              flexShrink: 0,
              px: "10px",
              pl: "12px",
              py: "6px",
              borderRadius: "6px",
              // Same colours as the theme's default <Button>: teal-600 fill,
              // white label (colors.js — teal-500 never carries white text).
              border: `1px solid ${teal[600]}`,
              background: teal[600],
              color: "#fff",
              fontSize: 11,
              fontFamily: FONT_MONO,
              fontWeight: 500,
              letterSpacing: "0.07em",
              textTransform: "uppercase",
              transition: "all 0.2s",
              height: "fit-content",
            }}
          >
            <span>View details</span>
            <Box
              component="span"
              className="open-indicator-arrow"
              sx={{ display: "inline-block", transition: "transform 0.2s" }}
            >
              →
            </Box>
          </Flx>
          </Flx>
        </Flx>

        {/* PRIMARY FIGURES — 4-cell connected box. FICO highlights green
            when the mid score is ≥720 to surface the strong-credit story. */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" },
            background: TOKENS.surface3,
            border: `1px solid ${TOKENS.line}`,
            borderRadius: "10px",
            overflow: "hidden",
          }}
        >
          <FigureCell
            label="FICO Mid"
            value={ficoMid || "N/A"}
            valueColor={ficoColor}
            highlight={ficoHighlight}
            idx={0}
          />
          <FigureCell
            label="Liquidity"
            value={fmtMoney(totalLiquidity)}
            idx={1}
          />
          <FigureCell
            label="Experience"
            value={`${fmtPoints(verifiedExp)} pts`}
            valueColor={expColor}
            idx={2}
          />
          <FigureCell
            label="Ownership"
            value={
              ownershipPct != null
                ? `${(Number(ownershipPct) * 100).toFixed(0)}%`
                : "—"
            }
            idx={3}
          />
        </Box>

        {/* SECONDARY FIGURES — sparse 4-cell row for the underwriting
            timestamps. Each cell is independently muted when the value
            is missing so a half-filled row reads as such at a glance. */}
        <Box
          sx={{
            mt: 1.5,
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, 1fr)",
              sm: `repeat(${borrower?.mortgage_lates ? 4 : 3}, 1fr)`,
            },
            // The three verification cards sit 12px apart, flush with the
            // figures box above.
            gap: 1.5,
          }}
        >
          {CARD_VERIFICATIONS.map((v) => (
            <VerificationField
              key={v.key}
              label={v.label}
              borrower={borrower}
              loanId={loanId}
              dateField={v.dateField}
              dateMode={v.dateMode}
              dateLabel={v.dateLabel}
              dateHelp={v.dateHelp}
              target={v.target}
              altTargets={v.altTargets}
              files={verificationDocs[v.key]?.files}
              candidates={verificationDocs[v.key]?.candidates}
            />
          ))}
          {borrower?.mortgage_lates && (
            <FigureSecondary
              label="Mortgage lates"
              value={
                mortgageLates > 0
                  ? fmtNumber(mortgageLates)
                  : Array.isArray(borrower?.mortgage_lates) ||
                      borrower?.mortgage_lates != null
                    ? "0"
                    : "—"
              }
              valueColor={mortgageLates > 0 ? TOKENS.urgent : undefined}
              muted={!mortgageLates && !borrower?.mortgage_lates}
            />
          )}
        </Box>

        {/* ASSOCIATED section hidden for now — linked properties / entities
            render as link chips at the end of the header chip row. */}

        {/* CREDITED EXPERIENCE — experience items tied to this applicant,
            like the General Contractors roster. Only when the loan uses the
            workbook and at least one item credits this person. */}
        {/* The two fold-able sections are one group, set a little further
            below the verification fields (32px on top of the column gap). */}
        {experience?.items?.length || priorLoans ? (
          <Flx column g={3} sx={{ mt: 4 }}>
            {experience?.items?.length ? (
              <ApplicantCreditedExperience {...experience} />
            ) : null}

            {/* PRIOR LOANS & SERVICING — this person's other EV / LQ loans
                with live FCI status (applicant-history.md). Information only;
                the section stops click propagation so expanding it doesn't
                open the borrower sidebar. Absent when the route isn't
                available. */}
            {priorLoans ? <PriorLoansSection {...priorLoans} /> : null}
          </Flx>
        ) : null}

        {/* BORROWER EXPERIENCE SUMMARY — only rendered when set. Read-only
            here; editing lives in the borrower sidebar's Info > Experience
            section. */}
        {borrower?.borrowerExperienceSummary ? (
          <Box sx={{ width: "max-content" }}>
            <Txt
              sx={{
                display: "block",
                mb: 1.25,
                fontSize: 10,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                color: TOKENS.ink3,
                fontWeight: 500,
              }}
            >
              Experience Summary
            </Txt>
            <Txt
              sx={{
                display: "block",
                fontSize: 12.5,
                color: TOKENS.ink2,
                lineHeight: 1.55,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {borrower.borrowerExperienceSummary}
            </Txt>
          </Box>
        ) : null}

        {/* BORROWER NOTES — only rendered when set. Read-only here; editing
            lives in the borrower sidebar's Notes section. */}
        {borrower?.additional_borrower_notes ? (
          <Box>
            <Txt
              sx={{
                display: "block",
                mb: 1.25,
                fontSize: 10,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                color: TOKENS.ink3,
                fontWeight: 500,
              }}
            >
              Notes
            </Txt>
            <Txt
              sx={{
                display: "block",
                fontSize: 12.5,
                color: TOKENS.ink2,
                lineHeight: 1.55,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {borrower.additional_borrower_notes}
            </Txt>
          </Box>
        ) : null}

        {/* STATUS BLOCK — pinned to the bottom of the left column. Tone
            derived from the borrower's underwriting flags: urgent when
            housing events / guideline checks exist, good when credit auth
            + background are both received, otherwise muted with a
            "Pending verifications" copy. */}
        <Box sx={{ mt: "auto" }}>
          <ApplicantStatusBlock
            borrower={borrower}
            creditAuthDate={creditAuthDate}
            backgroundDate={backgroundDate}
            creditReportDate={creditReportDate}
          />
        </Box>
      </Box>

      {/* ═══════════════════════════════════════════
          RIGHT COLUMN — borrower documents
          ═══════════════════════════════════════════ */}
      <Box
        sx={{
          background: TOKENS.surface,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          position: "relative",
        }}
      >
        {/* Trial: DocGroup Coverage view of the borrower's documents. To go
            back, render RecordDocumentsPanel (shared/) with the same props. */}
        <RecordDocCoveragePanel
          assignTo={{
            type: "borrower",
            id: borrower?._id,
            label: fullName || borrower?.emailAddress || "Borrower",
          }}
          files={borrowerFiles}
          linkedDocumentIds={borrower?.associations?.loanDocuments || []}
          // In flow: at least the card's height (the grid stretches it),
          // growing with its content up to DOC_PANEL_MAX_H, then scrolling.
          sx={{ maxHeight: DOC_PANEL_MAX_H }}
        />
      </Box>

      {/* Dialog content lives in a portal but synthetic events still bubble
          through the React tree. Without this guard, tab clicks inside the
          editor reach the outer Box's onClick (which selects the borrower)
          and trigger a re-render that snaps the editor back to its first
          tab. */}
      <Box
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <AssociationsEditor
          open={editorOpen}
          loan={loanDrilldown}
          initial={{}}
          existing={borrower?.associations || {}}
          enabledFields={ASSOCIATION_FIELDS}
          hostLabel={fullName || borrower?.emailAddress || "Borrower"}
          title="Add associations to borrower"
          saving={savingAssociations}
          onSave={handleSaveAssociations}
          onClose={() => setEditorOpen(false)}
        />
      </Box>
    </CardPaper>
  );
});

/* ═══════════════════════════════════════════════════════════
   APPLICANT CARD HELPERS
   ═══════════════════════════════════════════════════════════ */

const SUB_TAG_TONES = {
  muted: { bg: TOKENS.surface2, fg: TOKENS.ink2, border: TOKENS.line },
  info: { bg: TOKENS.infoSoft, fg: TOKENS.info, border: TOKENS.infoLine },
  warn: { bg: TOKENS.warnSoft, fg: TOKENS.warn, border: TOKENS.warnLine },
  urgent: { bg: TOKENS.urgentSoft, fg: TOKENS.urgent, border: "#e8c4c4" },
  good: { bg: TOKENS.goodSoft, fg: TOKENS.good, border: TOKENS.goodLine },
};

/**
 * Why this applicant has the grade they have. The grade and both point scores
 * are computed by the backend (`borrower_classification.py` →
 * `ClassificationConfig`, returned as `rtlBorrowerClassification` /
 * `rtlCreditDecisionScore` / `rtlExperienceScore`); this only explains them.
 * RTL scale: credit 700+ = 3, 680–699 = 1, under 680 or foreign national = 0;
 * experience 10+ = 7, 3–9 = 5, 0–2 = 1; A+ 7+, A 5+, B 2+, else C. Ground-up
 * grades off verified experience alone, so it gets its own sentence.
 */
const classTooltip = (borrower, productType) => {
  const grade = borrower?.rtlBorrowerClassification;
  const exp = Number(borrower?.verifiedExperienceTotal);
  const expText = Number.isFinite(exp) ? `${fmtPoints(exp)} verified` : "none verified";
  if (/ground/i.test(String(productType || ""))) {
    return {
      title: `Borrower class ${grade}`,
      lines: [
        `Ground-up scale — graded on verified experience (${expText}).`,
        "A+ 4+ · A 2+ · B 1+ (needs 700+ FICO) · otherwise C",
      ],
    };
  }
  // Same test as the backend: a permanent resident is not a foreign national.
  const isFn = String(borrower?.citizenshipStatus || "")
    .toLowerCase()
    .startsWith("foreign");
  const cp = borrower?.rtlCreditDecisionScore;
  const ep = borrower?.rtlExperienceScore;
  const pts = (n) => `${n} pt${n === 1 ? "" : "s"}`;
  const creditWhy = isFn
    ? "foreign national"
    : borrower?.ficoMid
      ? `FICO ${borrower.ficoMid}`
      : "no FICO";
  const lines = [];
  if (cp != null) lines.push(`Credit: ${pts(cp)} (${creditWhy})`);
  if (ep != null) lines.push(`Experience: ${pts(ep)} (${expText})`);
  if (cp != null && ep != null) lines.push(`Total: ${pts(cp + ep)}`);
  lines.push("A+ 7+ · A 5+ · B 2+ · C below 2");
  return { title: `Borrower class ${grade}`, lines };
};

const SubTag = ({ tone = "muted", kind, tooltip, children }) => {
  // Classification grade pills get their own per-grade palette so the
  // letter rating reads as a quality grade rather than a generic flag.
  if (kind === "class") {
    const c = CLASS_TONE[children] || SUB_TAG_TONES.muted;
    const pill = (
      <Box
        component="span"
        sx={{
          background: c.bg,
          border: `1px solid ${c.border}`,
          color: c.color || c.fg,
          px: "7px",
          py: "2px",
          borderRadius: "4px",
          fontSize: 10.5,
          fontFamily: FONT_MONO,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          fontWeight: 700,
          whiteSpace: "nowrap",
          cursor: tooltip ? "help" : undefined,
        }}
      >
        {children}
      </Box>
    );
    if (!tooltip) return pill;
    return (
      <Tooltip
        arrow
        title={
          <Box sx={{ fontSize: 12, lineHeight: 1.5 }}>
            <Box sx={{ fontWeight: 600, mb: 0.25 }}>{tooltip.title}</Box>
            {tooltip.lines.map((line) => (
              <Box key={line}>{line}</Box>
            ))}
          </Box>
        }
      >
        {pill}
      </Tooltip>
    );
  }
  const t = SUB_TAG_TONES[tone] || SUB_TAG_TONES.muted;
  return (
    <Box
      component="span"
      sx={{
        background: t.bg,
        border: `1px solid ${t.border}`,
        color: t.fg,
        px: "7px",
        py: "2px",
        borderRadius: "4px",
        fontSize: 10.5,
        fontFamily: FONT_MONO,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        fontWeight: 500,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </Box>
  );
};

// Cell in the primary 4-column figures bar. Mirrors PropertyCard's
// FigureCell so the stat-row treatment is identical between cards.
const FigureCell = ({ label, value, valueColor, highlight, idx }) => (
  <Box
    sx={{
      p: "14px 16px",
      borderRight: {
        xs: idx % 2 === 0 ? `1px solid ${TOKENS.line}` : "none",
        sm: idx < 3 ? `1px solid ${TOKENS.line}` : "none",
      },
      borderBottom: {
        xs: idx < 2 ? `1px solid ${TOKENS.line}` : "none",
        sm: "none",
      },
      minWidth: 0,
    }}
  >
    <Txt
      sx={{
        fontSize: 10,
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        color: TOKENS.ink3,
        fontWeight: 500,
        mb: "4px",
      }}
    >
      {label}
    </Txt>
    <Txt
      sx={{
        fontFamily: FONT_MONO,
        fontSize: 16,
        fontWeight: 500,
        color: highlight ? TOKENS.good : valueColor || TOKENS.ink,
        letterSpacing: "-0.01em",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {value}
    </Txt>
  </Box>
);

// Sparse secondary figure cell — no borders, smaller value font. When
// `files` are supplied (e.g. the credit-auth / credit-report / background
// document), each renders as a compact previewable link beneath the value;
// clicking opens the file-preview sidebar without selecting the card.
const FigureSecondary = ({
  label,
  value,
  muted,
  valueColor,
  relative,
  files,
}) => (
  <Box>
    <Txt
      sx={{
        fontSize: 10,
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        color: TOKENS.ink3,
        fontWeight: 500,
        mb: "4px",
      }}
    >
      {label}
    </Txt>
    <Txt
      sx={{
        fontFamily: FONT_MONO,
        fontSize: 14,
        fontWeight: 500,
        color: muted ? TOKENS.ink3 : valueColor || TOKENS.ink,
        letterSpacing: "-0.01em",
      }}
    >
      {value}
      {relative && (
        <Txt
          component="span"
          sx={{
            ml: 0.5,
            fontFamily: FONT_MONO,
            fontSize: 10,
            fontWeight: 400,
            color: TOKENS.ink4,
            letterSpacing: 0,
          }}
        >
          ({relative})
        </Txt>
      )}
    </Txt>
    {files?.length > 0 && (
      <Flx
        column
        g={0.25}
        sx={{ mt: 0.5, alignItems: "flex-start" }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {files.map((file) => (
          <PreviewableLoanFileButton
            key={file._id}
            file={file}
            fontSize={10.5}
            iconSize={12}
            maxWidth="100%"
            sx={{
              py: 0,
              px: 0.5,
              minHeight: 20,
              border: `1px solid ${TOKENS.infoLine}`,
              background: TOKENS.infoSoft,
              color: TOKENS.info,
              "&:hover": {
                background: TOKENS.infoSoft,
                borderColor: TOKENS.info,
              },
            }}
          />
        ))}
      </Flx>
    )}
  </Box>
);

// The applicant's linked properties / entities (ASSOCIATION_FIELDS — never
// documents), resolved to their records. Ids whose record is gone are skipped.
const useLinkedItems = (loan, associations) => {
  const enabled = useMemo(
    () => filterAssociationFields(ASSOCIATION_FIELDS),
    [],
  );
  return useMemo(() => {
    const out = [];
    for (const field of enabled) {
      const ids = associations?.[field.key] || [];
      if (!ids.length) continue;
      const source = field.getSource(loan) || [];
      for (const id of ids) {
        const rec = source.find((r) => r?._id === id);
        if (!rec) continue;
        out.push({
          key: `${field.key}-${id}`,
          label: field.getLabel(rec) || "—",
          typeLabel: field.label,
        });
      }
    }
    return out;
  }, [enabled, associations, loan]);
};

// A header chip for one association: link icon + what it is. Same shape as
// SubTag, but sentence case (it is a name, not a flag) and clickable — it
// opens the associations editor.
const LinkedTag = ({ item, onClick }) => (
  <Tooltip title={`Linked ${item.typeLabel}: ${item.label}`} arrow>
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        maxWidth: 240,
        background: TOKENS.surface,
        border: `1px solid ${TOKENS.line}`,
        color: TOKENS.ink2,
        px: "7px",
        py: "2px",
        borderRadius: "4px",
        fontSize: 11.5,
        fontFamily: "inherit",
        lineHeight: 1.4,
        cursor: "pointer",
        transition: "border-color 0.15s, color 0.15s",
        "&:hover": { borderColor: TOKENS.accent, color: TOKENS.accent },
      }}
    >
      <LinkRounded sx={{ fontSize: 13, flexShrink: 0, color: TOKENS.ink3 }} />
      <Box
        component="span"
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {item.label}
      </Box>
    </Box>
  </Tooltip>
);

const STATUS_BLOCK_TONES = {
  good: {
    bg: TOKENS.goodSoft,
    border: TOKENS.goodLine,
    iconBg: TOKENS.good,
    actionFg: TOKENS.good,
    actionBorder: "rgba(45,106,74,0.2)",
  },
  warn: {
    bg: TOKENS.warnSoft,
    border: TOKENS.warnLine,
    iconBg: TOKENS.warn,
    actionFg: TOKENS.warn,
    actionBorder: "rgba(184,100,29,0.2)",
  },
  urgent: {
    bg: TOKENS.urgentSoft,
    border: "#e8c4c4",
    iconBg: TOKENS.urgent,
    actionFg: TOKENS.urgent,
    actionBorder: "rgba(168,42,42,0.2)",
  },
  info: {
    bg: TOKENS.infoSoft,
    border: TOKENS.infoLine,
    iconBg: TOKENS.info,
    actionFg: TOKENS.info,
    actionBorder: "rgba(44,82,130,0.2)",
  },
  muted: {
    bg: TOKENS.surface2,
    border: TOKENS.line,
    iconBg: TOKENS.ink3,
    actionFg: TOKENS.ink2,
    actionBorder: TOKENS.line,
  },
};

const ApplicantStatusBlock = ({
  borrower,
  creditAuthDate,
  backgroundDate,
  creditReportDate,
}) => {
  const hasHousingEvents = Boolean(borrower?.housing_events);
  const guidelineChecks = borrower?.borrowerGuidelineChecks || [];
  // Verifications block hidden for now — the missing verifications are
  // emphasised on their own fields (VerificationField) instead. Only the
  // underwriting-flags variant still renders.
  if (!hasHousingEvents && guidelineChecks.length === 0) return null;
  const verifications = [creditAuthDate, backgroundDate, creditReportDate];
  const verificationsComplete = verifications.every(Boolean);
  const verificationsCount = verifications.filter(Boolean).length;

  // Pick the dominant status signal. Underwriting flags trump completion
  // status — a fully-verified borrower with a guideline check still
  // surfaces the urgent flag.
  let tone = "muted";
  let title = "Pending verifications";
  let meta;
  let action = "Review";
  let Icon = HourglassEmpty;

  if (hasHousingEvents || guidelineChecks.length > 0) {
    tone = "urgent";
    Icon = ReportProblemOutlined;
    const pieces = [];
    if (hasHousingEvents) pieces.push("Housing events on file");
    if (guidelineChecks.length > 0) {
      pieces.push(
        `${guidelineChecks.length} guideline check${
          guidelineChecks.length === 1 ? "" : "s"
        }`,
      );
    }
    title = "Underwriting flags";
    meta = pieces.join(" · ");
    action = "Review";
  } else if (verificationsComplete) {
    tone = "good";
    Icon = CheckCircleOutline;
    title = "Verifications — complete";
    const dates = [
      creditAuthDate ? `Credit auth ${fmtDateShort(creditAuthDate)}` : null,
      backgroundDate ? `Background ${fmtDateShort(backgroundDate)}` : null,
    ].filter(Boolean);
    meta = dates.join(" · ");
    action = "View";
  } else if (verificationsCount > 0) {
    tone = "info";
    Icon = HourglassEmpty;
    title = `Verifications — ${verificationsCount} of ${verifications.length}`;
    const missing = [];
    if (!creditAuthDate) missing.push("credit auth");
    if (!backgroundDate) missing.push("background");
    if (!creditReportDate) missing.push("credit report");
    meta = missing.length ? `Missing: ${missing.join(", ")}` : null;
    action = "Update";
  } else {
    tone = "muted";
    Icon = ErrorOutline;
    title = "Verifications — not started";
    meta = "Credit auth + background pending";
    action = "Start";
  }

  const t = STATUS_BLOCK_TONES[tone];

  return (
    <Box
      sx={{
        background: t.bg,
        border: `1px solid ${t.border}`,
        borderRadius: "10px",
        p: "14px 16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
      }}
    >
      <Flx ac g={1.5} sx={{ minWidth: 0, flex: 1 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "8px",
            background: t.iconBg,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: tone === "good" ? 18 : 16 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Txt
            sx={{
              fontSize: 13,
              fontWeight: 500,
              color: TOKENS.ink,
              mb: "1px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </Txt>
          {meta && (
            <Txt
              sx={{
                fontSize: 11.5,
                color: TOKENS.ink2,
                fontFamily: FONT_MONO,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {meta}
            </Txt>
          )}
        </Box>
      </Flx>
      <Box
        sx={{
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          color: tone === "urgent" ? "#fff" : t.actionFg,
          background: tone === "urgent" ? t.iconBg : "rgba(255,255,255,0.6)",
          fontWeight: 500,
          fontFamily: FONT_MONO,
          px: "10px",
          py: "5px",
          borderRadius: "4px",
          border: `1px solid ${tone === "urgent" ? t.iconBg : t.actionBorder}`,
          flexShrink: 0,
          whiteSpace: "nowrap",
        }}
      >
        {action}
      </Box>
    </Box>
  );
};

ApplicantCard.displayName = "ApplicantCard";

// VerifiedUserOutlined is imported but currently unused in this file —
// kept available for future status-block icon variants.
// eslint-disable-next-line no-unused-vars
const _VerifiedUserOutlinedRef = VerifiedUserOutlined;

export default ApplicantCard;
